import React from 'react';

/**
 * First-run welcome for the dashboard empty state. Teaches what Merlin does as a
 * short, scannable list rather than a wall of bullets, and points new users at
 * the MERODE resources.
 */

/** Minimal inline icons; they inherit the chip's primary colour via currentColor. */
const iconProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const DiagramIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="3" width="7" height="6" rx="1.5" />
    <rect x="14" y="15" width="7" height="6" rx="1.5" />
    <path d="M6.5 9v3.5a2 2 0 0 0 2 2H14" />
  </svg>
);

const ShieldCheckIcon = () => (
  <svg {...iconProps}>
    <path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const CodeIcon = () => (
  <svg {...iconProps}>
    <path d="M16 6l5 6-5 6" />
    <path d="M8 6l-5 6 5 6" />
  </svg>
);

const features = [
  {
    icon: <DiagramIcon />,
    title: 'Model your domain',
    body: 'UML class diagrams and finite state charts, side by side.',
  },
  {
    icon: <ShieldCheckIcon />,
    title: 'Stay MERODE-correct',
    body: 'Merlin reifies associations into existence dependencies and shares events across objects in an Object-Event table, checking your model as you go.',
  },
  {
    icon: <CodeIcon />,
    title: 'Ready for code',
    body: 'Validated models are set for code generation and prototyping.',
  },
];

const FirstTimeHelp = () => {
  return (
    <section className="help animate-[merlin-fade-up_0.3s_ease-out]">
      <h2 className="text-[26px] font-bold leading-tight text-black [text-wrap:balance]">
        Welcome to Merlin
      </h2>
      <p className="mb-0 mt-2 text-[15px] font-normal leading-relaxed text-grey2 [text-wrap:pretty]">
        Build MERODE domain models from UML class diagrams and finite state charts, validated as you
        work.
      </p>

      <ul className="mt-7 flex flex-col gap-5">
        {features.map((f) => (
          <li key={f.title} className="flex items-start gap-3.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              {f.icon}
            </span>
            <div>
              <div className="font-semibold text-black">{f.title}</div>
              <p className="mb-0 mt-0.5 text-sm font-normal leading-snug text-grey2 [text-wrap:pretty]">
                {f.body}
              </p>
            </div>
          </li>
        ))}
      </ul>

      <p className="mb-0 mt-7 border-t border-grey3 pt-5 text-sm font-normal text-grey2">
        New to MERODE? Watch the{' '}
        <a
          className="font-medium text-primary no-underline hover:text-primary-hover hover:underline"
          href="https://youtu.be/N8_z02lNWH8"
        >
          intro video
        </a>{' '}
        or read more at{' '}
        <a
          className="font-medium text-primary no-underline hover:text-primary-hover hover:underline"
          href="http://merode.econ.kuleuven.be/"
        >
          merode.econ.kuleuven.be
        </a>
        .
      </p>
    </section>
  );
};

export default FirstTimeHelp;
